/* global React, ReactDOM, Icon, fmtDec */
// ============================================================
// PLUMME 2.0 — UI primitives
// ============================================================
const {useState,useEffect,useRef,useMemo,useCallback}=React;

function Button(props){
  const { variant = 'primary', size, icon, iconRight, children, className = '' } = props;
  const p = Object.assign({}, props);
  delete p.variant;
  delete p.size;
  delete p.icon;
  delete p.iconRight;
  delete p.children;
  delete p.className;
  const cls=['btn','btn-'+variant,size==='sm'?'btn-sm':size==='lg'?'btn-lg':'',className].filter(Boolean).join(' ');
  return <button className={cls} {...p}>{icon&&<Icon name={icon} size={size==='sm'?16:18}/>}{children}{iconRight&&<Icon name={iconRight} size={size==='sm'?16:18}/>}</button>;
}
function IconButton(props){
  const { name, size = 'md', variant = '', className = '', iconSize } = props;
  const p = Object.assign({}, props);
  delete p.name;
  delete p.size;
  delete p.variant;
  delete p.className;
  delete p.iconSize;
  return <button className={['icon-btn',size==='sm'?'sm':'',variant,className].filter(Boolean).join(' ')} {...p}><Icon name={name} size={iconSize||(size==='sm'?16:19)}/></button>;
}
function QuickChoiceButtons({choices=[],activeKey='',onSelect,ariaLabel,className=''}){if(!choices.length)return null;return <div className={['quick-choice-pills',className].filter(Boolean).join(' ')} role="list" aria-label={ariaLabel}>{choices.map((choice)=>{const selected=choice.key===activeKey;return <button key={choice.key} type="button" className={'quick-choice-pill'+(selected?' active':'')} aria-pressed={selected} title={choice.title||undefined} disabled={choice.disabled} onClick={()=>onSelect?.(choice)}><span>{choice.label}</span>{choice.icon&&<Icon name={choice.icon} size={13}/>}</button>;})}</div>;}
function Field({label,hint,children,className='',style,help,helpTitle,'data-help':helpText,'data-help-title':helpHeading}){
  return <div className={'field '+className} style={style} {...(help||helpText?{'data-help':help||helpText,'data-help-title':helpTitle||helpHeading||(typeof label==='string'?label:undefined)}:{})}>{label&&<label>{label}</label>}{children}{hint&&<span className="hint">{hint}</span>}</div>;
}
function Input(props){
  const { icon, passwordToggle, className } = props;
  const [passwordVisible, setPasswordVisible] = useState(false);
  const canTogglePassword = passwordToggle && props.type === 'password';
  const decimal = window.PlummeDecimalInput.useInput(props, props.type === 'number' || props.inputMode === 'decimal', React);
  const p = Object.assign({}, props, decimal);
  delete p.icon;
  delete p.passwordToggle;
  // Klassen fra kalleren maa slaas sammen med `input`. Ble den spredt inn med
  // resten av proppene overskrev den basisklassen, og feltet mistet hele
  // Plumme-stilen og ble tegnet som et rått nettleserfelt.
  delete p.className;
  const inputClass = ['input', className].filter(Boolean).join(' ');
  if(canTogglePassword) p.type = passwordVisible ? 'text' : 'password';
  if(canTogglePassword) return <div className={['input-icon','input-password',icon?'':'no-icon'].filter(Boolean).join(' ')}>
    {icon&&<Icon name={icon} size={18}/>}
    <input className={inputClass} {...p}/>
    <button
      type="button"
      className="input-password-toggle"
      aria-label={passwordVisible ? t('auth.hidePassword') : t('auth.showPassword')}
      aria-pressed={passwordVisible}
      title={passwordVisible ? t('auth.hidePassword') : t('auth.showPassword')}
      disabled={p.disabled}
      onClick={() => setPasswordVisible((visible) => !visible)}
    >
      <Icon name={passwordVisible ? 'eyeOff' : 'eye'} size={18}/>
    </button>
  </div>;
  if(icon) return <div className="input-icon"><Icon name={icon} size={18}/><input className={inputClass} {...p}/></div>;
  return <input className={inputClass} {...p}/>;
}
const selectMenuViewportStyle = (rect, viewportWidth, viewportHeight, desiredHeight = 280) => {
  const margin = 12;
  const gap = 6;
  const availableBelow = Math.max(0, viewportHeight - rect.bottom - gap - margin);
  const availableAbove = Math.max(0, rect.top - gap - margin);
  const openUp = availableBelow < desiredHeight && availableAbove > availableBelow;
  const availableHeight = openUp ? availableAbove : availableBelow;
  const menuHeight = Math.max(72, Math.min(desiredHeight, availableHeight));
  const width = Math.min(rect.width, Math.max(0, viewportWidth - margin * 2));
  const left = Math.max(margin, Math.min(rect.left, viewportWidth - width - margin));
  return {
    position: 'fixed',
    top: openUp ? Math.max(margin, rect.top - gap - menuHeight) : rect.bottom + gap,
    left,
    right: 'auto',
    width,
    maxHeight: menuHeight,
    zIndex: 10000,
  };
};
const selectOptionModels = (children) => {
  const options = [];
  const visit = (nodes, group = '') => React.Children.forEach(nodes, (child) => {
    if (!React.isValidElement(child)) return;
    if (child.type === React.Fragment) {
      visit(child.props.children, group);
      return;
    }
    if (child.type === 'optgroup') {
      visit(child.props.children, child.props.label || group);
      return;
    }
    if (child.type !== 'option') return;
    const optionValue = child.props.value !== undefined ? child.props.value : child.props.children;
    options.push({
      value: String(optionValue),
      label: child.props.children,
      disabled: !!child.props.disabled,
      group,
    });
  });
  visit(children);
  return options;
};
function Select(props){
  const { children, value, defaultValue, onChange, disabled, className = '', style, name } = props;
  const p = Object.assign({}, props);
  delete p.children;
  delete p.value;
  delete p.defaultValue;
  delete p.onChange;
  delete p.disabled;
  delete p.className;
  delete p.style;
  const controlled = value !== undefined;
  const options = selectOptionModels(children);
  const initial = defaultValue !== undefined ? String(defaultValue) : (options[0]?.value || '');
  const [internalValue, setInternalValue] = useState(initial);
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const wrap = useRef(null);
  const menuRef = useRef(null);
  const currentValue = controlled ? String(value ?? '') : internalValue;
  const current = options.find((option) => option.value === currentValue) || options[0];

  const positionMenu = useCallback(() => {
    if (!wrap.current) return;
    const rect = wrap.current.getBoundingClientRect();
    const desiredHeight = Math.min(280, menuRef.current?.scrollHeight || options.length * 44 + 16);
    setMenuStyle(selectMenuViewportStyle(rect, window.innerWidth, window.innerHeight, desiredHeight));
  }, [options.length]);

  useEffect(() => {
    if (!open) return;
    positionMenu();
    const onDown = (event) => {
      if (wrap.current?.contains(event.target) || menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const onKey = (event) => { if (event.key === 'Escape') setOpen(false); };
    const onMove = () => positionMenu();
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [open, positionMenu]);

  const pick = (option) => {
    if (option.disabled) return;
    if (!controlled) setInternalValue(option.value);
    onChange && onChange({
      target: { value: option.value, name },
      currentTarget: { value: option.value, name },
    });
    setOpen(false);
  };

  const menu = open && !disabled && <div className="select-menu select-menu-portal" role="listbox" style={menuStyle||undefined} ref={menuRef}>
    {options.map((option, index) => <React.Fragment key={option.value}>
      {option.group && option.group !== options[index - 1]?.group && <div className="select-group-label">{option.group}</div>}
      <button
        type="button"
        className={'select-option' + (option.value === currentValue ? ' active' : '')}
        disabled={option.disabled}
        role="option"
        aria-selected={option.value === currentValue}
        onClick={() => pick(option)}
      >{option.label}</button>
    </React.Fragment>)}
  </div>;

  return <div className={['select-wrap', open ? 'open' : '', className].filter(Boolean).join(' ')} style={style} ref={wrap}>
    <button type="button" className="input select-trigger" disabled={disabled} aria-haspopup="listbox" aria-expanded={open} onClick={() => !disabled && setOpen((state) => !state)} {...p}>
      <span className="select-label">{current?.label || ''}</span>
      <Icon name="chevDown" size={16} className="select-chevron" />
    </button>
    {menu && ReactDOM?.createPortal ? ReactDOM.createPortal(menu, document.body) : menu}
  </div>;
}
// Searchable combobox with grouping, featured options, an optional action and a clipping-safe portal mode.
function SearchSelect({value,onChange,options,placeholder='Velg …',searchPlaceholder='Søk …',max=360,popupWidth,expandLabel='Vis flere',emptyLabel='Ingen treff',actionLabel,onAction,disabled=false,portal=false}){
  const [open,setOpen]=useState(false),[query,setQuery]=useState(''),[expanded,setExpanded]=useState(false),[menuStyle,setMenuStyle]=useState(null);
  const ref=useRef(null),menuRef=useRef(null);
  const opts=(options||[]).map(option=>Array.isArray(option)?{value:option[0],label:option[1]}:option),current=opts.find(option=>String(option.value)===String(value));
  const positionMenu=useCallback(()=>{
    if(!portal||!ref.current)return;
    const rect=ref.current.getBoundingClientRect(),width=Math.max(rect.width,Number(popupWidth)||0);
    const positionedRect=popupWidth?{left:rect.left,right:rect.left+width,top:rect.top,bottom:rect.bottom,width}:rect;
    const desiredHeight=Math.min(Number(max)||360,menuRef.current?.scrollHeight||Number(max)||360);
    setMenuStyle(selectMenuViewportStyle(positionedRect,window.innerWidth,window.innerHeight,desiredHeight));
  },[portal,max,popupWidth,opts.length,actionLabel]);
  useEffect(()=>{
    if(!open)return;positionMenu();
    const close=event=>{if(ref.current?.contains(event.target)||menuRef.current?.contains(event.target))return;setOpen(false);};
    const escape=event=>{if(event.key==='Escape')setOpen(false);},move=()=>positionMenu();
    document.addEventListener('mousedown',close);document.addEventListener('keydown',escape);
    window.addEventListener('resize',move);window.addEventListener('scroll',move,true);
    return()=>{document.removeEventListener('mousedown',close);document.removeEventListener('keydown',escape);window.removeEventListener('resize',move);window.removeEventListener('scroll',move,true);};
  },[open,positionMenu]);
  useEffect(()=>{if(open){setQuery('');setExpanded(false);}},[open]);
  const normalizedQuery=query.trim().toLocaleLowerCase('nb');
  const matches=option=>`${option.label||''} ${option.description||''} ${option.searchText||''} ${option.group||''} ${option.value||''}`.toLocaleLowerCase('nb').includes(normalizedQuery);
  const filtered=normalizedQuery?opts.filter(matches):opts;
  const featured=filtered.filter(option=>option.featured);
  const visible=normalizedQuery||expanded||featured.length===0?filtered:filtered.filter(option=>option.featured||String(option.value)===String(value));
  const hiddenCount=Math.max(0,filtered.length-visible.length);let previousGroup='';
  const popup=open&&!disabled&&<div className="ss-pop" style={portal?(menuStyle||{visibility:'hidden'}):{maxHeight:max}} ref={menuRef}>
    <div className="ss-search"><Input icon="search" autoFocus placeholder={searchPlaceholder} value={query} onChange={event=>setQuery(event.target.value)}/></div>
    <div className="ss-list" role="listbox">
      {visible.length===0?<div className="ss-empty">{emptyLabel}</div>:visible.map(option=>{
        const group=option.group||'',showGroup=group&&group!==previousGroup;previousGroup=group;
        return <React.Fragment key={option.value}>
          {showGroup&&<div className="ss-group-label">{group}</div>}
          <button type="button" className={'ss-item'+(String(option.value)===String(value)?' on':'')} role="option" aria-selected={String(option.value)===String(value)} onClick={()=>{onChange&&onChange(option.value);setOpen(false);}}>
            <span className="ss-option-copy"><span>{option.label}</span>{option.description&&<small>{option.description}</small>}</span>
            {String(option.value)===String(value)&&<Icon name="check" size={15}/>}
          </button>
        </React.Fragment>;
      })}
      {!normalizedQuery&&!expanded&&hiddenCount>0&&<button type="button" className="ss-expand" onClick={()=>setExpanded(true)}>{expandLabel} ({hiddenCount})</button>}
    </div>
    {actionLabel&&onAction&&<div className="ss-action-wrap"><button type="button" className="ss-action" onClick={()=>{setOpen(false);onAction();}}><Icon name="plus" size={17}/>{actionLabel}</button></div>}
  </div>;
  return <div ref={ref} className="search-select ss-wrap">
    <button type="button" className="input ss-control" disabled={disabled} aria-haspopup="listbox" aria-expanded={open} onClick={()=>!disabled&&setOpen(state=>!state)}>
      <span className={current?'':'ss-ph'}>{current?current.label:placeholder}</span><Icon name={open?'chevUp':'chevDown'} size={16}/>
    </button>
    {portal&&popup&&ReactDOM?.createPortal?ReactDOM.createPortal(<div className="search-select ss-portal-host">{popup}</div>,document.body):popup}
  </div>;
}
function Currency(props){
  const { suffix = 'kr', className, maxDecimals = 3 } = props;
  const p = { ...props, ...window.PlummeDecimalInput.useInput({...props, maxDecimals}, true, React) };
  delete p.suffix; delete p.className; delete p.maxDecimals;
  return <div className="input-affix"><input className={['input', className].filter(Boolean).join(' ')} inputMode="decimal" {...p}/><span className="affix">{suffix}</span></div>;
}
function Switch({on,onChange,disabled=false}){return <button className={'switch'+(on?' on':'')} disabled={disabled} onClick={()=>!disabled&&onChange(!on)} role="switch" aria-checked={on} aria-disabled={disabled}/>;}

function Segmented({options,value,onChange,soft,block}){
  return <div className={'seg'+(soft?' soft':'')+(block?' block':'')}>
    {options.map(o=>{const v=o.value??o,l=o.label??o;
      return <button key={v} className={value===v?'active':''} onClick={()=>onChange(v)}>{o.icon&&<Icon name={o.icon} size={16}/>}{l}</button>;})}
  </div>;
}
// Segmented som måler den faktiske bredden på knappene og folder de som ikke får
// plass inn i en "Mer"-meny, i stedet for å flyte utenfor beholderen. Samme mønster
// som FakturaTabs i invoice-list.jsx, men for enkle value/label-options.
function SegmentedOverflow({options,value,onChange,soft,moreLabel}){
  const wrapRef=useRef(null),measRef=useRef(null);
  const [vis,setVis]=useState(options.length);
  const [open,setOpen]=useState(false);
  const measure=useCallback(()=>{
    const wrap=wrapRef.current,meas=measRef.current;
    if(!wrap||!meas) return;
    const avail=wrap.clientWidth;
    const widths=Array.from(meas.querySelectorAll('[data-sw]')).map(b=>b.offsetWidth);
    const moreEl=meas.querySelector('[data-sw-more]');
    const moreW=moreEl?moreEl.offsetWidth:0;
    const gap=4,pad=10;
    if(pad+widths.reduce((a,w)=>a+w+gap,0)-gap+pad<=avail){setVis(options.length);return;}
    let used=pad+moreW+gap,n=0;
    for(let i=0;i<widths.length;i++){const w=widths[i]+gap; if(used+w<=avail){used+=w;n++;}else break;}
    setVis(Math.max(1,n));
  },[options.length]);
  React.useLayoutEffect(()=>{measure();},[measure,value]);
  useEffect(()=>{
    measure();
    const wrap=wrapRef.current;
    if(!wrap||typeof ResizeObserver==='undefined'){window.addEventListener('resize',measure);return()=>window.removeEventListener('resize',measure);}
    const ro=new ResizeObserver(measure); ro.observe(wrap); return()=>ro.disconnect();
  },[measure]);
  const norm=options.map(o=>({value:o.value??o,label:o.label??o,icon:o.icon}));
  const visible=norm.slice(0,vis),hidden=norm.slice(vis);
  const pick=(v)=>{onChange(v);setOpen(false);};
  const label=moreLabel||t('ui.more');
  return <div className="segx" ref={wrapRef}>
    <div className={'seg segx-row'+(soft?' soft':'')}>
      {visible.map(o=><button key={o.value} className={value===o.value?'active':''} onClick={()=>pick(o.value)}>{o.icon&&<Icon name={o.icon} size={16}/>}{o.label}</button>)}
      {hidden.length>0&&<div className="popwrap" style={{display:'inline-flex'}}>
        <button className={'segx-more'+(hidden.some(o=>o.value===value)?' active':'')+(open?' open':'')} onClick={()=>setOpen(o=>!o)}>{label}<Icon name="chevDown" size={14}/></button>
        {open&&<React.Fragment><div className="pop-scrim" onClick={()=>setOpen(false)}/>
          <div className="pop pop-menu right" style={{minWidth:210}}>
            {hidden.map(o=><button key={o.value} onClick={()=>pick(o.value)}>{o.icon&&<Icon name={o.icon} size={16}/>}{o.label}{value===o.value&&<Icon name="check" size={16} style={{marginLeft:'auto'}}/>}</button>)}
          </div></React.Fragment>}
      </div>}
    </div>
    <div className="seg segx-row segx-meas" ref={measRef} aria-hidden="true">
      {norm.map(o=><button key={'m-'+o.value} data-sw>{o.icon&&<Icon name={o.icon} size={16}/>}{o.label}</button>)}
      <button data-sw-more className="segx-more">{label}<Icon name="chevDown" size={14}/></button>
    </div>
  </div>;
}
// Grov peker = finger. Dette er den riktige aksen for drag-and-drop: en tablet i
// bredt vindu har fortsatt ingen musepeker, så skjermbredde alene er feil signal.
function useCoarsePointer(){
  const query='(pointer: coarse)';
  const [coarse,setCoarse]=useState(()=>typeof window.matchMedia==='function'&&window.matchMedia(query).matches);
  useEffect(()=>{
    if(typeof window.matchMedia!=='function')return;
    const mq=window.matchMedia(query);
    const onChange=(event)=>setCoarse(event.matches);
    if(mq.addEventListener)mq.addEventListener('change',onChange); else mq.addListener(onChange);
    return()=>{if(mq.removeEventListener)mq.removeEventListener('change',onChange); else mq.removeListener(onChange);};
  },[]);
  return coarse;
}
function Tabs({options,value,onChange}){
  const ref=useRef(null),mounted=useRef(false);
  // Når fanene scroller horisontalt (mobil), flyttes den aktive til starten av raden.
  useEffect(()=>{
    const row=ref.current,btn=row&&row.querySelector('button.active');
    let box=row&&row.parentElement;
    while(box&&box!==document.body&&!/(auto|scroll)/.test(getComputedStyle(box).overflowX))box=box.parentElement;
    const first=!mounted.current;mounted.current=true;
    if(!btn||!box||box===document.body)return;
    if(box.scrollWidth>box.clientWidth){
      const gap=parseFloat(getComputedStyle(box).paddingLeft)||0;
      const left=box.scrollLeft+btn.getBoundingClientRect().left-box.getBoundingClientRect().left-gap;
      box.scrollTo({left:Math.max(0,left),behavior:first?'auto':'smooth'});
    }
    // Mobil: ved fanebytte løftes hele fanekortet til toppen, rett under topplinjen.
    if(!first&&box.matches('.aii-tabs,.emp-tabwrap')&&window.matchMedia('(max-width:620px)').matches){
      const bar=document.querySelector('.topbar');
      const top=box.getBoundingClientRect().top-(bar?bar.getBoundingClientRect().bottom:0)-8;
      window.scrollBy({top,behavior:'smooth'});
    }
  },[value]);
  return <div className="tabs" ref={ref}>{options.map(o=>{const v=o.value??o,l=o.label??o;
    return <button key={v} className={value===v?'active':''} onClick={()=>onChange(v)}>{l}</button>;})}</div>;
}

function Badge({variant='neutral',dot,children}){
  return <span className={'badge badge-'+variant}>{dot&&<span className="dot"/>}{children}</span>;
}
// Bare farge her — etiketten slås opp på badge.<status> ved render, ellers ville
// den vært låst til språket som gjaldt da fila ble evaluert.
const STATUS_MAP={
  betalt:'success', paid:'success', aktiv:'success',
  // Akseptert tilbud og bekreftet ordre er et ja fra kunden, ikke en nooytral
  // mellomstasjon. De sto graa og forsvant i lista blant «sendt».
  akseptert:'success', bekreftet:'success',
  sendt:'neutral', utkast:'warning', draft:'warning', opprettet:'warning',
  forfalt:'error', overdue:'error', kreditert:'error',
  passiv:'muted', ok:'success', ikke:'error', snart:'warning',
};
function StatusBadge({status,label}){
  return <Badge variant={STATUS_MAP[status]||'muted'} dot>{label||t(PlummeI18n.keyFor('badge', status, 'status'),status)}</Badge>;}

function Card({children,className='',pad=true,style}){
  return <div className={'card '+(pad?'card-pad ':'')+className} style={style}>{children}</div>;
}
function CardHead({icon,title,right,help,helpTitle}){
  return <div className="card-hd">
    {icon&&<span className="icon-chip"><Icon name={icon} size={19}/></span>}
    <h3 className="t-h3 grow" style={{fontSize:18}} {...(help?{'data-help':help,'data-help-title':helpTitle||title}:{})}>{title}</h3>{right}</div>;
}
function SectionTitle({children,right}){
  return <div className="row between" style={{margin:'4px 2px 14px'}}><h2 className="t-h1">{children}</h2>{right}</div>;
}

function KPI({tint='white',label,value,sub,trend,icon,help}){
  return <div className={'kpi kpi-'+tint} {...(help?{'data-help':help,'data-help-title':label}:{})}>
    <div className="kpi-top">{icon?<span className="kpi-ico"><Icon name={icon} size={16}/></span>:<span className="kpi-dot"/>}{label}</div>
    <div className="kpi-val">{value}</div>
    {sub&&<div className="kpi-sub">{trend!=null&&<Icon name={trend>=0?'trendUp':'trendDown'} size={14} className={trend>=0?'pos':'neg'}/>}<span className={trend>0?'pos':trend<0?'neg':'muted'}>{sub}</span></div>}
  </div>;
}

// Belopene her er totalen paa et bilag, ikke et nokkeltall. De skal vises med ore:
// dokumentet som sendes ut bruker `fmtDec`, og en avrundet sum i oppsummeringen
// var derfor et annet tall enn det kunden faktisk faar.
function MathSummary({net,vat,title,currency}){
  const total=net+vat;
  return <Card style={{background:'linear-gradient(135deg,var(--card) 55%,var(--plum-50))'}}>
    <CardHead icon="calculator" title={title||t('ui.summary.title')}/>
    <div className="math-tiles">
      <div className="math-tile"><div className="math-tile-label">{t('ui.summary.exVat')}</div><div className="math-tile-val t-num">{currency?window.PlummeI18n.money(net,{currency,decimals:2}):fmtDec(net)}</div></div>
      <span className="math-op">+</span>
      <div className="math-tile"><div className="math-tile-label">{t('ui.summary.vat')}</div><div className="math-tile-val t-num">{currency?window.PlummeI18n.money(vat,{currency,decimals:2}):fmtDec(vat)}</div></div>
    </div>
    <div className="math-total">
      <span className="math-total-l"><span className="math-eq">=</span>{t('ui.summary.total')}</span>
      <span className="math-total-v t-num">{currency?window.PlummeI18n.money(total,{currency,decimals:2}):fmtDec(total)}</span>
    </div>
  </Card>;
}
function Stepper({steps,current,onJump,incomplete}){
  const inc=incomplete||[];
  return <div className="stepper">{steps.map((s,i)=>{
    const isDone=i<current && !inc.includes(i);
    const cls=isDone?'done':i===current?'active':'';
    const flag=i<current && inc.includes(i);
    return <div key={i} className={'step '+cls+(flag?' incomplete':'')}>
      <button className="row gap-10" style={{background:'none',cursor:onJump?'pointer':'default'}} onClick={()=>onJump&&onJump(i)}>
        <span className="scircle">{isDone?<Icon name="check" size={16}/>:i+1}</span>
        <span className="slabel">{s}</span>
      </button>
      {i<steps.length-1&&<span className="sline"/>}
    </div>;})}</div>;
}

// center=true midtstiller dialogen på mobil i stedet for å forankre den i bunnen.
// Brukes der det nederste feltet må være lett å nå, som i «Slå sammen».
function Modal({open,onClose,title,titleRight,children,footer,max,maxHeight,portal=false,center=false,top=false,className='',style}){
  useEffect(()=>{if(!open)return;const h=e=>e.key==='Escape'&&onClose();window.addEventListener('keydown',h);return()=>window.removeEventListener('keydown',h);},[open,onClose]);
  if(!open)return null;
  const modalStyle={...(max?{maxWidth:max}:{}),...(maxHeight?{maxHeight}: {}),...style};
  const content=<div className={'overlay'+(center?' overlay-center':'')+(top?' overlay-top':'')} onMouseDown={e=>e.target===e.currentTarget&&onClose()}>
    <div className={'modal'+(className?` ${className}`:'')} style={max||maxHeight||style?modalStyle:null}>
      <div className="modal-hd"><h3 className="t-h2">{title}</h3><div className="row gap-12" style={{alignItems:'center'}}>{titleRight}<IconButton name="x" size="sm" variant="ghost" onClick={onClose}/></div></div>
      <div className="modal-bd">{children}</div>
      {footer&&<div className="modal-ft">{footer}</div>}
    </div></div>;
  return portal&&ReactDOM?.createPortal ? ReactDOM.createPortal(content,document.body) : content;
}
// Delt bekreftelsesdialog. API-et folger designkildens `ConfirmModal`, med to
// tillegg appen faktisk trenger: `busy` (mutasjonen gaar mot backend og tar tid)
// og at `onConfirm` kan vaere async. Kaster den, holdes dialogen aapen slik at
// brukeren kan prove igjen — feilmeldingen kommer fra handleren som toast.
function ConfirmModal({open=true,onClose,onConfirm,title=t('common.confirmTitle'),message,children,confirmLabel,cancelLabel,danger=true,busy=false,confirmIcon,max=460}){
  const run=async()=>{
    try{await onConfirm();onClose();}
    catch(error){/* handleren viser toast og lar dialogen staa for nytt forsok */}
  };
  const close=busy?()=>{}:onClose;
  return <Modal open={open} onClose={close} title={title} max={max} className="confirm-modal"
    footer={<React.Fragment>
      <Button variant="ghost" disabled={busy} onClick={close}>{cancelLabel||t('common.cancel')}</Button>
      <Button variant={danger?'danger-solid':'primary'} icon={confirmIcon||(danger?'trash':'check')} disabled={busy} onClick={run}>{busy?t('common.working'):(confirmLabel||t('common.continue'))}</Button>
    </React.Fragment>}>
    <div className="row gap-12" style={{alignItems:'flex-start'}}>
      {danger&&<div className="confirm-modal-icon"><Icon name="alertTri" size={20}/></div>}
      <p className="t-body" style={{margin:danger?'4px 0 0':0}}>{children||message}</p>
    </div>
  </Modal>;
}
function Drawer({open,onClose,title,children,footer,width}){
  useEffect(()=>{if(!open)return;const h=e=>e.key==='Escape'&&onClose();window.addEventListener('keydown',h);return()=>window.removeEventListener('keydown',h);},[open,onClose]);
  if(!open)return null;
  return <React.Fragment>
    <div data-app-control="true" className="drawer-ov" onClick={onClose}/>
    <div className="drawer" style={width?{width}:null}>
      <div className="modal-hd" style={{padding:'20px 22px',borderBottom:'1px solid var(--border)'}}><h3 className="t-h2">{title}</h3><IconButton name="x" size="sm" variant="ghost" onClick={onClose}/></div>
      <div style={{padding:'20px 22px',overflow:'auto',flex:1}}>{children}</div>
      {footer&&<div className="modal-ft" style={{borderTop:'1px solid var(--border)'}}>{footer}</div>}
    </div></React.Fragment>;
}

// ---- Toasts ----
const _toastL=new Set();
function toast(msg,type='success'){_toastL.forEach(fn=>fn(msg,type));}
function ToastHost(){
  const [items,setItems]=useState([]);
  useEffect(()=>{const fn=(msg,type)=>{const id=Math.random();setItems(x=>[...x,{id,msg,type}]);
    setTimeout(()=>setItems(x=>x.filter(i=>i.id!==id)),3600);};
    _toastL.add(fn);return()=>_toastL.delete(fn);},[]);
  const ico={success:'checkCircle',error:'alertCircle',warning:'alertTri',info:'info'};
  // NB: iterasjonsvariabelen kan ikke hete `t` — den ville skygge for den globale t().
  const content=<div className="toast-wrap">{items.map(item=>
    <div key={item.id} className={'toast '+item.type}>
      <span style={{color:`var(--${item.type==='info'?'plum-600':item.type})`,flex:'none',marginTop:1}}><Icon name={ico[item.type]} size={20}/></span>
      <div className="grow" style={{fontWeight:700,fontSize:14}}>{item.msg}</div>
      <button className="icon-btn ghost sm" onClick={()=>setItems(x=>x.filter(i=>i.id!==item.id))}><Icon name="x" size={15}/></button>
    </div>)}</div>; return ReactDOM?.createPortal ? ReactDOM.createPortal(content,document.body) : content;
}

function EmptyState({acc='none',mood='happy',title,text,action,compact,img,noVisual=false}){
  return <div className="col center" style={{textAlign:'center',padding:compact?'30px 20px':'48px 24px',gap:6}}>
    {!noVisual && img && <img src={img} alt="" style={{width:compact?150:190,height:'auto',display:'block'}}/>}
    <h3 className="t-h2" style={{marginTop:(!noVisual && img)?8:0}}>{title}</h3>
    {text&&<p className="muted" style={{maxWidth:380,fontWeight:600}}>{text}</p>}
    {action&&<div className="mt-12">{action}</div>}
  </div>;
}

function Pagination({page,pages,onPage,total,from,to,pageSize,onPageSize,pageSizes=[10,25,50]}){
  const nums=useMemo(()=>{
    const clamp=(n)=>Math.max(1,Math.min(pages,n));
    if(pages<=7){const all=[];for(let i=1;i<=pages;i++)all.push(i);return all;}
    const current=clamp(page);
    const middleStart=Math.max(2,Math.min(current-1,pages-4));
    const middleEnd=Math.min(pages-1,Math.max(current+1,5));
    const items=[1];
    if(middleStart>2)items.push('gap-start');
    for(let i=middleStart;i<=middleEnd;i++)items.push(i);
    if(middleEnd<pages-1)items.push('gap-end');
    items.push(pages);
    return items;
  },[page,pages]);
  return <div className="row wrap gap-12" style={{padding:'4px 2px',justifyContent:'flex-end'}}>
    {onPageSize&&<select className="input" style={{height:36,width:72,fontSize:13}} value={pageSize} onChange={e=>onPageSize(+e.target.value)}>
      {pageSizes.map(n=><option key={n} value={n}>{n}</option>)}</select>}
    <span className="t-sm muted">{from}–{to} / {total}</span>
    <div className="pager">
      <button className="pg" disabled={page<=1} onClick={()=>onPage(page-1)}><Icon name="chevLeft" size={16}/></button>
      {nums.map(n=>typeof n==='number'
        ? <button key={n} className={'pg'+(n===page?' active':'')} onClick={()=>onPage(n)}>{n}</button>
        : <span key={n} className="t-sm muted" style={{padding:'0 4px',fontWeight:800}}>...</span>)}
      <button className="pg" disabled={page>=pages} onClick={()=>onPage(page+1)}><Icon name="chevRight" size={16}/></button>
    </div>
  </div>;
}

function FilterBar({children,onReset}){
  const [open,setOpen]=useState(false);
  return <div>
    <div className="row gap-12">
      <button className={'btn btn-secondary btn-sm'+(open?' ':'')} onClick={()=>setOpen(o=>!o)}><Icon name="filter" size={16}/>{t('ui.filter')}</button>
      <button className="t-sm" style={{color:'var(--muted)',textDecoration:'underline',background:'none',fontWeight:700}} onClick={onReset}>{t('ui.filterReset')}</button>
    </div>
    {open&&<div className="panel mt-12" style={{padding:'16px 18px'}}><div className="row wrap gap-16" style={{alignItems:'flex-end'}}>{children}</div></div>}
  </div>;
}

function ProgressBar({value,color='var(--warning)'}){
  return <div className="bar"><span style={{width:Math.min(100,Math.max(0,value))+'%',background:color}}/></div>;
}

function ChipToggle({options,value,onChange}){
  return <div className="chiptog">{options.map(o=><button key={o.value} className={value===o.value?'active':''} onClick={()=>onChange(o.value)}>{o.label}</button>)}</div>;
}

// ---------------- Shared filter ----------------
function Checkbox({checked,onChange,children}){
  return <label className="chk">
    <input type="checkbox" checked={!!checked} onChange={e=>onChange(e.target.checked)}/>
    <span className="box">{checked&&<Icon name="check" size={14} stroke={3}/>}</span>
    <span>{children}</span>
  </label>;
}
// Resolve labels at render time: lazy English catalogs load after data.jsx, so
// retaining the original MONTHS values would freeze Norwegian abbreviations.
const filterMonthLabels=()=>MONTHS.map((month,index)=>t('month.short.'+(index+1),month));
function makeFilter(over){return {from:'',to:'',months:[0,11],checks:{},amtOn:false,amtFrom:'',amtTo:'',...over};}
function filterActive(f){return !!(f.from||f.to||f.projectId||f.departmentId||(f.sort&&f.sort!=='statement-order')||f.months[0]!==0||f.months[1]!==11||(f.amtOn&&(f.amtFrom||f.amtTo))||Object.values(f.checks).some(Boolean));}
function dateInFilter(ddmmyyyy,f){
  if(!ddmmyyyy||ddmmyyyy==='—')return true;
  const [d,m,y]=ddmmyyyy.split('.').map(Number);
  if(!y||!m)return true;
  const iso=`${y}-${String(m).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
  if(f.from&&iso<f.from)return false;
  if(f.to&&iso>f.to)return false;
  const mi=m-1;if(mi<f.months[0]||mi>f.months[1])return false;
  return true;
}
function FilterMonthRange({months,onChange}){
  const trackRef=useRef(null);const drag=useRef(null);
  const [from,to]=months,monthLabels=filterMonthLabels();
  const posOf=i=>(i/11)*100;
  const clickMonth=i=>onChange(Math.abs(i-from)<=Math.abs(i-to)?[Math.min(i,to),to]:[from,Math.max(i,from)]);
  const singleMonth=i=>onChange([i,i]);
  const onDown=which=>e=>{e.preventDefault();drag.current=which;
    const move=ev=>{const t=trackRef.current;if(!t)return;const x=ev.touches?ev.touches[0].clientX:ev.clientX;const r=t.getBoundingClientRect();
      let p=Math.round(((x-r.left)/r.width)*11);p=Math.max(0,Math.min(11,p));
      onChange(which==='from'?[Math.min(p,to),to]:[from,Math.max(p,from)]);};
    const up=()=>{drag.current=null;window.removeEventListener('pointermove',move);window.removeEventListener('pointerup',up);};
    window.addEventListener('pointermove',move);window.addEventListener('pointerup',up);};
  return <div className="period">
    <div className="period-head"><span className="t-h3" style={{fontSize:15}}>{t('ui.period')}</span>
      <span className="t-sm" style={{fontWeight:800,color:'var(--plum-700)'}}>{monthLabels[from]}–{monthLabels[to]}</span></div>
    <div className="period-track" ref={trackRef}>
      <div className="period-fill" style={{left:posOf(from)+'%',width:(posOf(to)-posOf(from))+'%'}}/>
      <div className="period-thumb" style={{left:posOf(from)+'%'}} onPointerDown={onDown('from')} role="slider" aria-label={t('ui.fromMonth')}/>
      <div className="period-thumb" style={{left:posOf(to)+'%'}} onPointerDown={onDown('to')} role="slider" aria-label={t('ui.toMonth')}/>
    </div>
    <div className="period-months">{monthLabels.map((m,i)=><span data-app-control="true" key={m} className={i>=from&&i<=to?'on':''} onClick={()=>clickMonth(i)} onDoubleClick={()=>singleMonth(i)}>{m}</span>)}</div>
  </div>;
}
const FILTER_TERMS=[
  {n:1,label:'Termin 1',m:[0,1]},{n:2,label:'Termin 2',m:[2,3]},{n:3,label:'Termin 3',m:[4,5]},
  {n:4,label:'Termin 4',m:[6,7]},{n:5,label:'Termin 5',m:[8,9]},{n:6,label:'Termin 6',m:[10,11]},
];
const termLabel=(term)=>t('ui.term',term.label,{n:term.n});
function FilterMonthRangeV2({value,onChange,year=2026}){
  const trackRef=useRef(null),monthLabels=filterMonthLabels();
  const scrollRef=useRef(null);
  const years=[year-1,year,year+1];
  const nMonths=years.length*12;
  const COLW=46;
  const y0=years[0];
  const pad2=n=>String(n).padStart(2,'0');
  const dimOf=(y,m)=>new Date(y,m+1,0).getDate();
  const parseIso=iso=>{const [y,m,d]=(iso||'').split('-').map(Number);return {y,m:m-1,d};};
  const monthStart=(y,m)=>`${y}-${pad2(m+1)}-01`;
  const monthEnd=(y,m)=>`${y}-${pad2(m+1)}-${pad2(dimOf(y,m))}`;
  const fromIso=value.from||monthStart(year,value.months?value.months[0]:0);
  const toIso=value.to||monthEnd(year,value.months?value.months[1]:11);
  const fO=parseIso(fromIso),tO=parseIso(toIso);
  const pctStart=o=>(((o.y-y0)*12+o.m)+(o.d-1)/dimOf(o.y,o.m))/nMonths*100;
  const pctEnd=o=>(((o.y-y0)*12+o.m)+o.d/dimOf(o.y,o.m))/nMonths*100;
  const emit=(a,b)=>{
    if(a>b){const t=a;a=b;b=t;}
    const fa=parseIso(a),tb=parseIso(b);
    onChange({...value,from:a,to:b,months:fa.y===tb.y?[fa.m,tb.m]:[0,11]});
  };
  const fracToIso=f=>{
    f=Math.max(0,Math.min(nMonths-1e-6,f));
    const ma=Math.floor(f),y=y0+Math.floor(ma/12),m=ma%12,dim=dimOf(y,m);
    let day=Math.floor((f-ma)*dim)+1;day=Math.max(1,Math.min(dim,day));
    return `${y}-${pad2(m+1)}-${pad2(day)}`;
  };
  const now=new Date();
  const curY=now.getFullYear();
  const showToday=curY>=y0&&curY<=years[years.length-1];
  const absToday=(curY-y0)*12+now.getMonth()+(now.getDate()-1)/dimOf(curY,now.getMonth());
  const todayIso=`${curY}-${pad2(now.getMonth()+1)}-${pad2(now.getDate())}`;
  const onDown=which=>e=>{
    e.preventDefault();
    const move=ev=>{
      const t=trackRef.current;if(!t)return;
      const x=ev.touches?ev.touches[0].clientX:ev.clientX;
      const r=t.getBoundingClientRect();
      const iso=fracToIso((x-r.left)/r.width*nMonths);
      if(which==='from')emit(iso>toIso?toIso:iso,toIso);
      else emit(fromIso,iso<fromIso?fromIso:iso);
    };
    const up=()=>{window.removeEventListener('pointermove',move);window.removeEventListener('pointerup',up);};
    window.addEventListener('pointermove',move);window.addEventListener('pointerup',up);
  };
  useEffect(()=>{const s=scrollRef.current;if(s)s.scrollLeft=12*COLW*(year-y0)-COLW;},[]);
  const curTerm=curY===year?Math.floor(now.getMonth()/2):0;
  const quicks=[
    {label:t('ui.quick.wholeYear'),f:monthStart(year,0),t:monthEnd(year,11)},
    {label:t('ui.quick.ytd'),f:monthStart(year,0),t:curY===year?todayIso:monthEnd(year,11)},
    {label:t('ui.quick.thisTerm'),f:monthStart(year,FILTER_TERMS[curTerm].m[0]),t:monthEnd(year,FILTER_TERMS[curTerm].m[1])},
    {label:t('ui.quick.lastYear'),f:monthStart(year-1,0),t:monthEnd(year-1,11)},
  ];
  const isActive=q=>q.f===fromIso&&q.t===toIso;
  const seg=o=>`${o.d}. ${monthLabels[o.m]}`;
  const rangeLabel=fO.y===tO.y?`${seg(fO)} – ${seg(tO)} ${tO.y}`:`${seg(fO)} ${fO.y} – ${seg(tO)} ${tO.y}`;
  return <div className="period">
    <div className="period-head"><span className="t-h3" style={{fontSize:15}}>{t('ui.period')}</span>
      <span className="t-sm" style={{fontWeight:800,color:'var(--plum-700)'}}>{rangeLabel}</span></div>
    <div className="period-quicks">
      {quicks.map(q=><button key={q.label} type="button" className={'period-quick'+(isActive(q)?' on':'')} onClick={()=>emit(q.f,q.t)}>{q.label}</button>)}
    </div>
    <div className="period-scroll" ref={scrollRef}>
      <div className="period-cal" ref={trackRef} style={{width:nMonths*COLW}}>
        <div className="period-fill-band" style={{left:pctStart(fO)+'%',width:pctEnd(tO)-pctStart(fO)+'%'}}/>
        <div className="period-years" style={{gridTemplateColumns:`repeat(${years.length},1fr)`}}>
          {years.map(y=>{const on=monthEnd(y,11)>=fromIso&&monthStart(y,0)<=toIso;return <button key={y} type="button" className={'period-year'+(y===year?' base':'')+(on?' on':'')} onClick={()=>emit(monthStart(y,0),monthEnd(y,11))} title={t('ui.wholeYearTitle',{year:y})}>{y}</button>;})}
        </div>
        <div className="period-terms" style={{gridTemplateColumns:`repeat(${years.length*6},1fr)`}}>
          {/* NB: iterasjonsvariabelen kan ikke hete `t` her — den ville skygge for den globale t(). */}
          {years.map((y,yi)=>FILTER_TERMS.map((term,ti)=>{const on=monthEnd(y,term.m[1])>=fromIso&&monthStart(y,term.m[0])<=toIso;return <button key={y+'-t'+ti} type="button" className={'period-term'+(on?' on':'')+(ti===0&&yi>0?' yb':'')} onClick={()=>emit(monthStart(y,term.m[0]),monthEnd(y,term.m[1]))} title={termLabel(term)+' '+y}>{termLabel(term)}</button>;}))}
        </div>
        <div className="period-mgrid" style={{gridTemplateColumns:`repeat(${nMonths},1fr)`}}>
          {years.map((y,yi)=>monthLabels.map((m,mi)=>{const on=monthEnd(y,mi)>=fromIso&&monthStart(y,mi)<=toIso;return <button key={y+'-m'+mi} type="button" className={'period-mcell'+(on?' on':'')+(mi===0&&yi>0?' yb':'')} onClick={()=>emit(monthStart(y,mi),monthEnd(y,mi))} title={m+' '+y}>{m}</button>;}))}
        </div>
        {showToday&&<div className="period-today" style={{left:absToday/nMonths*100+'%'}}><span>{t('ui.today')}</span></div>}
        <div className="period-thumb cal" style={{left:pctStart(fO)+'%'}} onPointerDown={onDown('from')} role="slider" aria-label={t('ui.fromDate')}><b className="period-thumb-tip">{fO.d}.{pad2(fO.m+1)}</b></div>
        <div className="period-thumb cal" style={{left:pctEnd(tO)+'%'}} onPointerDown={onDown('to')} role="slider" aria-label={t('ui.toDate')}><b className="period-thumb-tip">{tO.d}.{pad2(tO.m+1)}</b></div>
      </div>
    </div>
  </div>;
}
function monthStartKey(y,m){return `${y}-${String(m+1).padStart(2,'0')}-01`;}
function monthEndKey(y,m){return `${y}-${String(m+1).padStart(2,'0')}-${String(new Date(y,m+1,0).getDate()).padStart(2,'0')}`;}
function monthIdxFromKey(v){const m=Number((v||'').slice(5,7));return Number.isFinite(m)&&m>=1?Math.max(0,Math.min(11,m-1)):0;}
// Filterpanelet henger visuelt under knappen, men bor i en portal på <body>.
// Inne i en dialog klipper `.modal-bd` (overflow:auto) et absolutt plassert
// panel bort — det var slik Månedskontroll mistet nedre halvdel av filteret.
// Når panelet først er løsnet fra ankeret må plasseringen regnes mot viewporten:
// høyrekant mot knappen, og oppover i stedet for nedover når det er trangt.
const filterPanelViewportStyle=(rect,vw,vh,panelWidth=0,panelHeight=0)=>{
  const margin=12,gap=8;
  const width=Math.min(panelWidth,Math.max(0,vw-margin*2));
  const base={position:'fixed',right:'auto',overflowY:'auto',overscrollBehavior:'contain',zIndex:10000};
  // Mobil: panelet er bredere enn knappen og lever over bunnmenyen, så det
  // sentreres i viewporten i stedet for å henge på et anker nær skjermkanten.
  if(vw<=610){
    const top=Math.round(vh*0.08);
    return {...base,top,bottom:'auto',left:Math.max(margin,Math.round((vw-width)/2)),
      maxHeight:Math.max(180,vh-top-104)};
  }
  const below=Math.max(0,vh-rect.bottom-gap-margin);
  const above=Math.max(0,rect.top-gap-margin);
  const openUp=below<Math.min(panelHeight||320,320)&&above>below;
  const left=Math.max(margin,Math.min(rect.right-width,vw-width-margin));
  return openUp
    ?{...base,left,top:'auto',bottom:Math.max(margin,vh-rect.top+gap),maxHeight:Math.max(180,above)}
    :{...base,left,top:Math.min(rect.bottom+gap,vh-margin),bottom:'auto',maxHeight:Math.max(180,below)};
};
function FilterPanel({value,onChange,onReset,dates,period,amount,groups,label,year=2026,children,help}){
  const [open,setOpen]=useState(false);
  const wrap=useRef(null);
  const panelRef=useRef(null);
  const [panelStyle,setPanelStyle]=useState(null);
  const positionPanel=useCallback(()=>{
    const anchorEl=wrap.current;
    if(!anchorEl)return;
    const panel=panelRef.current;
    setPanelStyle(filterPanelViewportStyle(anchorEl.getBoundingClientRect(),window.innerWidth,window.innerHeight,
      panel?.offsetWidth||0,panel?.scrollHeight||0));
  },[]);
  React.useLayoutEffect(()=>{if(open)positionPanel();else setPanelStyle(null);},[open,positionPanel]);
  useEffect(()=>{
    if(!open)return;
    const onKey=event=>{if(event.key==='Escape')setOpen(false);};
    const onMove=()=>positionPanel();
    document.addEventListener('keydown',onKey);
    window.addEventListener('resize',onMove);
    window.addEventListener('scroll',onMove,true);
    return()=>{
      document.removeEventListener('keydown',onKey);
      window.removeEventListener('resize',onMove);
      window.removeEventListener('scroll',onMove,true);
    };
  },[open,positionPanel]);
  const set=patch=>onChange({...value,...patch});
  const active=filterActive(value);
  // keep Fra/Til and the period picker in sync
  const setFrom=v=>{if(!v){set({from:''});return;}const mi=monthIdxFromKey(v);onChange({...value,from:v,months:[mi,Math.max(mi,value.months[1])]});};
  const setTo=v=>{if(!v){set({to:''});return;}const mi=monthIdxFromKey(v);onChange({...value,to:v,months:[Math.min(value.months[0],mi),mi]});};
  // Første render måles utenfor skjermen, slik at bredden fra CSS kan brukes
  // til høyrejusteringen uten at panelet blinker på feil plass.
  const measuring={position:'fixed',top:-9999,left:-9999,right:'auto',zIndex:10000,visibility:'hidden'};
  const panel=open&&<React.Fragment>
    <div className="pop-scrim" style={{zIndex:9999}} onClick={()=>setOpen(false)}/>
    <div className="filter-panel" ref={panelRef} style={panelStyle||measuring}>
      <div className="row between" style={{marginBottom:14}}>
        <span className="t-sm muted" style={{fontWeight:800}}>{t('ui.filter')}</span>
        <button className="filter-reset" onClick={onReset}>{t('ui.filterReset')}</button>
      </div>
      {dates&&<div className="grid-2 gap-14" style={{marginBottom:16}}>
        <Field label={t('ui.from')}><Input type="date" value={value.from||monthStartKey(year,value.months[0])} onChange={e=>setFrom(e.target.value)}/></Field>
        <Field label={t('ui.to')}><Input type="date" value={value.to||monthEndKey(year,value.months[1])} onChange={e=>setTo(e.target.value)}/></Field>
      </div>}
      {period&&<div style={{marginBottom:(amount||groups&&groups.length)?18:0}}><FilterMonthRangeV2 value={value} onChange={onChange} year={year}/></div>}
      {amount&&<div className="filter-group">
        <div className="row between" style={{alignItems:'center'}}>
          <span className="filter-group-label" style={{marginBottom:0}}>{t('ui.amountSearch')}</span>
          <Switch on={!!value.amtOn} onChange={v=>set({amtOn:v})}/>
        </div>
        {value.amtOn&&<div className="grid-2 gap-14" style={{marginTop:10}}>
          <Field label={t('ui.amountFrom')}><Currency value={value.amtFrom||''} onChange={e=>set({amtFrom:e.target.value})} placeholder="0"/></Field>
          <Field label={t('ui.amountTo')}><Currency value={value.amtTo||''} onChange={e=>set({amtTo:e.target.value})} placeholder="0"/></Field>
        </div>}
      </div>}
      {groups&&groups.map(g=><div key={g.label} className="filter-group">
        <div className="filter-group-label">{g.label}</div>
        <div className="filter-checks">{g.options.map(o=>
          <Checkbox key={o.key} checked={!!value.checks[o.key]} onChange={c=>set({checks:{...value.checks,[o.key]:c}})}>{o.label}</Checkbox>)}</div>
      </div>)}
      {children}
    </div>
  </React.Fragment>;
  return <div className="popwrap filter-wrap" ref={wrap}>
    <button className={'btn btn-secondary'+(active?' filter-on':'')} onClick={()=>setOpen(o=>!o)} {...(help?{'data-help':help,'data-help-title':t('ui.filterChoices')}:{})}>
      <Icon name="filter" size={16}/><span className="ph-btn-label">{label||t('ui.filterChoices')}</span>{active&&<span className="filter-dot"/>}<Icon name={open?'chevUp':'chevDown'} size={15}/>
    </button>
    {panel&&(ReactDOM?.createPortal?ReactDOM.createPortal(panel,document.body):panel)}
  </div>;
}

function signedAmountColor(value){
  const amount=Number(value)||0;
  if(amount>0.005)return 'var(--success)';
  if(amount<-0.005)return 'var(--error)';
  return 'var(--ink)';
}

Object.assign(window,{Button,IconButton,QuickChoiceButtons,Field,Input,Select,SearchSelect,Currency,Switch,Segmented,Tabs,Badge,StatusBadge,Card,CardHead,SectionTitle,KPI,MathSummary,Stepper,Modal,ConfirmModal,Drawer,toast,ToastHost,EmptyState,Pagination,FilterBar,ProgressBar,ChipToggle,Checkbox,FilterPanel,FilterMonthRange,makeFilter,filterActive,dateInFilter,signedAmountColor});
